import React, {
  createRef,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { DeleteIcon } from '@sharedCR/coreDesignSystem/icons';
import { documentApi, documentsApi } from 'app/services/api';
import ButtonWithTooltip from 'app/shared/components/common/ButtonWithTooltip';
import Skeleton from 'app/shared/components/common/Skeleton';
import clsx from 'clsx';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { ConfirmationModalType } from 'constants/confirmationDialog';
import { IconSize } from 'constants/coreDesignSystem';
import {
  AiDataRecognitionResult,
  DOCUMENT_DEFAULT_SCALE,
  DOCUMENT_FIELDS_BLOCK_WIDTH,
  DOCUMENT_MAX_ROTATION,
  DOCUMENT_ROTATION_STEP,
  DOCUMENT_THUMBNAILS_BLOCK_WIDTH_CLOSED,
  DOCUMENT_THUMBNAILS_BLOCK_WIDTH_OPEN,
  DocumentRotationDirection,
  DOCUMENTS_LIST_WIDTH,
  EXTRACTION_REQUEST_INCLUDES,
  SCALES,
} from 'constants/documents';
import { PAGE_CODES } from 'constants/pageCodes';
import { SKELETON_TYPES } from 'constants/skeleton';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import usePrevious from 'hooks/usePrevious';
import { getScrollStyles } from 'styles/scrollbar.styles';
import { checkIsGuid, getConditionalArray } from 'utils/data';
import { reorder } from 'utils/dnd';
import {
  checkIsGenAi,
  checkIsMalwareBlocked,
  getDocumentInitialScale,
  getPdfPageBaseSizes,
  loadPdfWithBaseSizes,
} from 'utils/documents';
import { jsonStringify } from 'utils/json';
import { logger } from 'utils/logger';
import { getPdfJsDocument } from 'utils/pdf';

import {
  getAiExtractionResultAction,
  getDocumentByIdAction,
  updateDocumentContentAction,
} from 'store/documents/actions';
import { getAiExtractionModelsGroupedByLayers, getDocumentById } from 'store/documents/selectors';
import { openConfirmationDialogAction, showMessageAction } from 'store/global/actions';
import { getFieldsTreeGroupedByAiExtractionFieldName } from 'store/system/selectors';

import { DocumentTestIds } from '../document.testIds';

import AiModelResultOverlay from './AiModelResultOverlay';
import BoundingBoxPopUp from './BoundingBoxPopUp';
import ControlPanel from './ControlPanel';
import DocumentBlockedNotice from './DocumentBlockedNotice';
import { aiExtractionResultOptions } from './DocumentView.constants';
import { getStyles } from './DocumentView.styles';
import {
  checkIsMouseInsideBoundingBox,
  checkIsPageInRenderWindow,
  createLayoutFieldKey,
  displayFieldBoundingBox,
  getBoundingBoxColors,
  getBoundingBoxCoords,
  getBoxQuadrant,
  getInitialReadingAnchor,
  getMostVisiblePageNumber,
  getReadingPageState,
  getScrolledPageState,
  getScrollInContainerToPage,
  getThumbnailAnchorId,
  getUnloadedDocumentState,
  setBoundingBoxPopUpPosition,
} from './DocumentView.utils';
import { useReadingAnchor } from './hooks/useReadingAnchor';
import PdfCard from './PdfCard';
import { releaseCanvasBackingStore } from './PdfCard/PdfCard.utils';
import ThumbnailControlPanel from './ThumbnailControlPanel';

const defaultObject = {};

const INITIAL_STATE = {
  pdf: null,
  pageBaseSizes: null,
  readingPageIndex: 0,
  scale: DOCUMENT_DEFAULT_SCALE,
  rotationByIndex: [],
  selectedPage: [1],
  isPageSaving: false,
  pageOrder: null,
  isReorderSaving: false,
};

const PAGE_KEY = 'page';

const DocumentView = withLoading(
  ({
    docId = '',
    contentWidth,
    isView = false,
    loanParams = defaultObject,
    initialSelectedPage = null,
    afterSplitCb,
    setIsLoading,
    isLoading,
    isControlVisible,
    isDownloadable,
    isPrintable,
    isViewHistoryAvailable,
    isOpenInNewTabAllowed,
    isThumbnailsVisible,
    supressErrorMessage,
    viewWrapperClassName: __viewWrapperClassName, // eslint-disable-line no-unused-vars
    classes: inlineClasses,
    getDocumentCall = documentsApi.getContent,
    onDownloadDocument,
    onPrintDocument,
    onOpenDocumentInNewTab,
    onDocumentLoaded,
    resetDocumentId = _.noop,
    transferRef = defaultObject,
    isAiExtractionResultDisplayed = false,
    setAiModelFieldToFillInForm,
    boundingBoxPopUpInfo,
    setBoundingBoxPopUpInfo,
    locatedFieldSource = null,
    isFocusMode = false,
    onToggleFocus = null,
  }) => {
    // Two overlay modes share this viewer. MAPPING mode (the document-data editor, which wires an
    // onFill handler) draws every field box and shows a hover picker to assign a value to a field.
    // LOCATE mode (the loan-editor inspector, no onFill handler) keeps the canvas clean and only
    // highlights the single field the user clicked in the fields list — driven by locatedFieldSource.
    const isFieldMappingMode = !_.isNil(setAiModelFieldToFillInForm);
    const { pathname } = useLocation();
    const document = useSelector(getDocumentById(docId));
    const isMalwareBlocked = checkIsMalwareBlocked(document?.isMalware);
    const aiAnalysisResult = useSelector(getAiExtractionModelsGroupedByLayers(docId));
    const extractionFieldsGroupedByAiExtractionFieldName = useSelector(
      getFieldsTreeGroupedByAiExtractionFieldName,
    );

    const theme = useTheme();

    const documentTypeId = document?.documentTypeId;
    const documentFileName = document?.fileName;

    const savedIndex = useRef(1);
    const wrapperRef = useRef(null);
    const pdfPagesWrapperRef = useRef(null);
    const thumbnailsWrapperRef = useRef(null);
    const selectedPageRef = useRef(null);
    const currentPdfRef = useRef(null);
    const isLowerLargeScreenRef = useRef(null);
    const PDFDocumentLoadingTaskRef = useRef(null);
    const pendingThumbnailScrollPageRef = useRef(null);

    isLowerLargeScreenRef.current = useMediaQuery(theme => theme.breakpoints.down('lg'));

    const [isPagesListOpen, setIsPagesListOpen] = useState(true);
    const [
      {
        pdf,
        pageBaseSizes,
        readingPageIndex,
        scale,
        rotationByIndex,
        selectedPage,
        isPageSaving,
        pageOrder,
        isReorderSaving,
      },
      setState,
    ] = useState(INITIAL_STATE);
    const [reachedBoundingBoxesGrouped, setReachedBoundingBoxesGrouped] = useState();
    const [aiModelResultOverlayRefs, setAiModelResultOverlayRefs] = useState([]);
    const [pdfCardRefs, setCardRefs] = useState([]);
    const [popUpRefs, setPopUpRefs] = useState([]);
    // LOCATE mode only: the popup shown for the single clicked field, and whether the user
    // dismissed it (so the highlight clears until they click another field).
    const [locatedFieldPopUp, setLocatedFieldPopUp] = useState(null);
    const [isLocateDismissed, setIsLocateDismissed] = useState(false);

    // In Focus mode the page thumbnails also collapse (to maximize the viewer),
    // without discarding the user's manual thumbnails-open preference.
    const isPagesListVisible = isPagesListOpen && !isFocusMode;

    const scrollClasses = getScrollStyles(theme);
    const classes = getStyles(theme, {
      contentWidth,
      thumbnailsWidth: isPagesListVisible
        ? DOCUMENT_THUMBNAILS_BLOCK_WIDTH_OPEN
        : DOCUMENT_THUMBNAILS_BLOCK_WIDTH_CLOSED,
      fieldsWidth: !isView ? DOCUMENT_FIELDS_BLOCK_WIDTH : DOCUMENTS_LIST_WIDTH,
    });

    const documentViewWrapperSx = useMemo(
      () => [classes.documentViewWrapper, scrollClasses.scroll, inlineClasses?.documentViewWrapper],
      [classes.documentViewWrapper, scrollClasses.scroll, inlineClasses?.documentViewWrapper],
    );
    const documentSlideSx = useMemo(
      () => [classes.documentSlide, scrollClasses.scroll],
      [classes.documentSlide, scrollClasses.scroll],
    );
    const documentViewSx = useMemo(
      () => [classes.documentView, scrollClasses.scroll],
      [classes.documentView, scrollClasses.scroll],
    );
    const documentViewContainerSx = useMemo(
      () => [classes.documentViewContainer, inlineClasses?.documentViewContainer],
      [classes.documentViewContainer, inlineClasses?.documentViewContainer],
    );

    const dispatch = useDispatch();

    const prevDocId = usePrevious(docId);

    const isExtractionRequestAllowed = !pathname.includes(_.toLower(PAGE_CODES.FORMS));

    const numPages = _.get(pdf, 'numPages', 0);
    const naturalPageOrder = useMemo(
      () => Array.from({ length: numPages }, (__, index) => index),
      [numPages],
    );
    const effectivePageOrder = pageOrder ?? naturalPageOrder;
    const displayedPageCount = effectivePageOrder.length;
    // A page save swaps in the uploaded document only after its upload resolves, so a reorder or
    // delete started meanwhile would rebuild from the stale document and overwrite the save.
    const isPageEditInFlight = isReorderSaving || isPageSaving;
    const currentPage = selectedPage[0];

    selectedPageRef.current = selectedPage;
    currentPdfRef.current = pdf;
    const refIndex = currentPage - 1;
    const isDisabledSplitButton =
      isLoading ||
      isPageEditInFlight ||
      displayedPageCount === 1 ||
      displayedPageCount === selectedPage.length ||
      !isControlVisible;
    const isDocumentViewNotRendered = (!docId && !isLoading) || (pdf && !numPages);
    const firstSelectedPage = _.head(selectedPage);

    const checkedAiModelLayersInitialState = useMemo(() => {
      return aiExtractionResultOptions.reduce(
        (acc, { value }) => ({
          ...acc,
          [value]: { value, isChecked: value === AiDataRecognitionResult.LAYOUT },
        }),
        {},
      );
    }, []);

    const [checkedAiLayers, setCheckedAiLayers] = useState(checkedAiModelLayersInitialState);

    const aiExtractionResult = useMemo(() => {
      if (!aiAnalysisResult) {
        return null;
      }

      let result;
      const checkedAiModelLayersValues = Object.values(checkedAiLayers);
      const checkedLayers = checkedAiModelLayersValues.filter(({ isChecked }) => !!isChecked);
      const isSingleLayerChecked = checkedLayers.length === 1;
      const isAllChecked =
        !isSingleLayerChecked && checkedAiModelLayersValues.every(({ isChecked }) => !!isChecked);

      if (isSingleLayerChecked) {
        const checkedLayer = checkedLayers[0];
        result = aiAnalysisResult[checkedLayer.value];
      }

      if (isAllChecked) {
        result = aiAnalysisResult[AiDataRecognitionResult.ALL_LAYERS];
      }

      return result || null;
    }, [aiAnalysisResult, checkedAiLayers]);

    const isPagesRotated = useMemo(() => rotationByIndex.filter(Boolean).length, [rotationByIndex]);
    const scrollInPagesContainerToPage = useMemo(
      () => getScrollInContainerToPage(pdfPagesWrapperRef),
      [],
    );
    const scrollInThumbnailsContainerToPage = useMemo(
      () => getScrollInContainerToPage(thumbnailsWrapperRef),
      [],
    );
    const setReadingPageIndex = useCallback(
      index => setState(prevState => getReadingPageState(prevState, index)),
      [],
    );
    const { setReadingAnchor, checkIsRestoreScroll, onReadingAnchorScroll } = useReadingAnchor({
      containerRef: pdfPagesWrapperRef,
      isPagesRendered: !!pdf && !isLoading,
      pdf,
      scale,
      rotationByIndex,
      pageOrder,
      onRestore: setReadingPageIndex,
    });

    //this is object is created to track whether the mouse has reached any of the bounding boxes and hasn't left it yet, it's then used in redrawing fn to avoid infinite re-renders
    const aiExractionFieldsReachedByMouseGrouped = useMemo(
      () =>
        aiExtractionResult?.pages.reduce((acc, page) => {
          const fieldsReachedByMouseGrouped = page.fields.reduce((acc, { type, value }, index) => {
            const key = type?.name || createLayoutFieldKey(value, index);

            return {
              ...acc,
              [key]: { isMouseReachedBoundingBox: false },
            };
          }, {});

          return {
            ...acc,
            [`${PAGE_KEY}${page.number}`]: { ...page, fields: fieldsReachedByMouseGrouped },
          };
        }, {}),
      [aiExtractionResult?.pages],
    );

    const aiExractionFieldsGroupedByPage = useMemo(
      () =>
        aiExtractionResult?.pages.reduce((acc, page) => {
          return {
            ...acc,
            [`${PAGE_KEY}${page.number}`]: page,
          };
        }, {}),
      [aiExtractionResult?.pages],
    );

    // LOCATE mode always resolves against the MAPPED_AI_MODEL_FIELDS layer specifically — NOT
    // aiExtractionResult, which tracks the layer-toggle UI (a MAPPING-mode concept defaulting to
    // Layout, whose fields carry no `schema`/`isMappedOnExtractionField` at all).
    const mappedAiModelFieldsByPage = useMemo(
      () =>
        aiAnalysisResult?.[AiDataRecognitionResult.MAPPED_AI_MODEL_FIELDS]?.pages?.reduce(
          (acc, page) => ({ ...acc, [`${PAGE_KEY}${page.number}`]: page }),
          {},
        ),
      [aiAnalysisResult],
    );

    // LOCATE mode: resolve the clicked list field to its AI-layer box on that page. A mapped AI
    // box is keyed by `schema.name`, which holds the field's human-readable label — the same value
    // the row carries as `fieldName` — so match on that first, then fall back to the extracted
    // value. Null = no box to highlight (the viewer still scrolls to the page).
    const locatedField = useMemo(() => {
      if (isFieldMappingMode || isLocateDismissed || !locatedFieldSource) {
        return null;
      }

      const { pageNumber, fieldValue, fieldName } = locatedFieldSource;
      const page = mappedAiModelFieldsByPage?.[`${PAGE_KEY}${pageNumber}`];
      const pageFields = page?.fields;

      if (!page || !pageFields) {
        return null;
      }

      const normalize = value => _.toLower(_.trim(_.toString(value ?? '')));
      const mappedFields = pageFields.filter(
        ({ isMappedOnExtractionField }) => isMappedOnExtractionField,
      );
      const fieldByName = mappedFields.find(
        ({ schema, type }) => normalize((schema ?? type)?.name) === normalize(fieldName),
      );
      const fieldByValue = _.isNil(fieldValue)
        ? null
        : mappedFields.find(({ value }) => normalize(value) === normalize(fieldValue));
      const matchedField = fieldByName ?? fieldByValue ?? null;

      return matchedField ? { field: matchedField, pageNumber, pageSize: page.size } : null;
    }, [isFieldMappingMode, isLocateDismissed, locatedFieldSource, mappedAiModelFieldsByPage]);

    const handleOpenInNewTab = useCallback(() => {
      onOpenDocumentInNewTab(docId);
    }, [docId, onOpenDocumentInNewTab]);

    const onSelectAiLayer = useCallback(e => {
      const { aiLayerValue } = e.currentTarget.dataset;

      setCheckedAiLayers(prev => ({
        ...prev,
        [aiLayerValue]: { isChecked: !prev[aiLayerValue].isChecked, value: aiLayerValue },
      }));
    }, []);

    //this fn checks the position of the mouse relative to the bounding boxes and whether it's inside any of the box and hasn't left it yet or not. Following on from that old bounding boxes are erased and the new ones are displayed. And when the mouse reaches any of the box, the pop up is displayed at the specific position
    const redrawBoundingBoxes = useCallback(
      ({ mouseX, mouseY, fields, size }) => {
        const canvas = aiModelResultOverlayRefs.length
          ? aiModelResultOverlayRefs[refIndex].current
          : null;
        const pdfCard = pdfCardRefs.length ? pdfCardRefs[refIndex].current : null;
        const pdfCardWidth = pdfCard.clientWidth;
        const pdfCardHeight = pdfCard.clientHeight;

        if (!canvas || !pdfCardWidth || !pdfCardHeight || !reachedBoundingBoxesGrouped) {
          return;
        }

        canvas.width = pdfCardWidth;
        canvas.height = pdfCardHeight;

        const context = canvas.getContext('2d');
        context.clearRect(0, 0, pdfCardWidth, pdfCardHeight);

        fields.forEach(
          (
            {
              boundingRegions,
              isMappedOnExtractionField,
              schema: fieldNameContainer,
              value,
              confidence,
              itemValues,
            },
            index,
          ) => {
            const boundingBoxColors = getBoundingBoxColors({
              theme,
              isMappedOnExtractionField,
              isLayout: !fieldNameContainer,
            });

            boundingRegions.forEach(({ boundingPolygon }) => {
              const fieldName = fieldNameContainer?.name || createLayoutFieldKey(value, index);
              const isHoveredBoundingBox = checkIsMouseInsideBoundingBox({
                mouseX,
                mouseY,
                boundingPolygon,
                pdfCard,
                pageSize: size,
              });
              const pageFields = reachedBoundingBoxesGrouped[`${PAGE_KEY}${currentPage}`]?.fields;
              const isMouseReachedBoundingBox = pageFields[fieldName]?.isMouseReachedBoundingBox;

              if (isHoveredBoundingBox && !isMouseReachedBoundingBox) {
                setReachedBoundingBoxesGrouped(prev =>
                  _.merge({}, prev, {
                    [`${PAGE_KEY}${currentPage}`]: {
                      fields: {
                        [fieldName]: { isMouseReachedBoundingBox: true },
                      },
                    },
                  }),
                );

                const coordsInPixels = getBoundingBoxCoords({
                  boundingPolygon,
                  pdfCard,
                  pageSize: size,
                });
                const { upperLeft } = coordsInPixels;
                const quadrant = getBoxQuadrant({
                  x: upperLeft.coordX,
                  y: upperLeft.coordY,
                  pdfCard,
                });
                const extractionField =
                  extractionFieldsGroupedByAiExtractionFieldName[fieldNameContainer?.name];

                setBoundingBoxPopUpInfo({
                  coords: coordsInPixels,
                  type: fieldNameContainer,
                  itemValues,
                  value,
                  confidence,
                  quadrant,
                  extractionField,
                });
              }

              if (!isHoveredBoundingBox && isMouseReachedBoundingBox) {
                setReachedBoundingBoxesGrouped(prev =>
                  _.merge({}, prev, {
                    [`${PAGE_KEY}${currentPage}`]: {
                      fields: {
                        [fieldName]: { isMouseReachedBoundingBox: false },
                      },
                    },
                  }),
                );
              }

              displayFieldBoundingBox({
                isMouseInBoundingBox: isHoveredBoundingBox,
                boundingPolygon,
                pdfCard,
                context,
                boundingBoxColors,
                pageSize: size,
              });
            });
          },
        );
      },
      [
        reachedBoundingBoxesGrouped,
        aiModelResultOverlayRefs,
        pdfCardRefs,
        setBoundingBoxPopUpInfo,
        extractionFieldsGroupedByAiExtractionFieldName,
        currentPage,
        refIndex,
        theme,
      ],
    );

    const onMouseMove = useCallback(
      event => {
        if (aiExtractionResult) {
          const canvas = event.target;
          const mouseX = event.clientX - canvas.getBoundingClientRect().left;
          const mouseY = event.clientY - canvas.getBoundingClientRect().top;
          const page = aiExractionFieldsGroupedByPage[`${PAGE_KEY}${currentPage}`];
          const { fields, size } = page || {};

          redrawBoundingBoxes({ mouseX, mouseY, fields, size });
        }
      },
      [redrawBoundingBoxes, aiExractionFieldsGroupedByPage, currentPage, aiExtractionResult],
    );

    useEffectSafeStrictMode(() => {
      if (!docId || !isExtractionRequestAllowed || !checkIsGuid(docId)) {
        return;
      }

      dispatch(
        getDocumentByIdAction({ documentId: docId, include: EXTRACTION_REQUEST_INCLUDES }),
      ).then(
        action =>
          checkIsGenAi(action?.payload) &&
          dispatch(getAiExtractionResultAction({ documentId: docId })),
      );
    }, [dispatch, docId, isExtractionRequestAllowed]);

    // Runs in the commit that renders the loaded thumbnails. A setTimeout(0) from the loader can
    // fire before React commits the document (WebKit runs it ahead of React's render task).
    useLayoutEffect(() => {
      const pageNumber = pendingThumbnailScrollPageRef.current;

      if (!pdf || isLoading || !pageNumber) {
        return;
      }

      pendingThumbnailScrollPageRef.current = null;
      scrollInThumbnailsContainerToPage(pageNumber);
    }, [pdf, isLoading, scrollInThumbnailsContainerToPage]);

    useEffect(() => {
      [setAiModelResultOverlayRefs, setCardRefs, setPopUpRefs].forEach(setRef => {
        setRef(refs =>
          Array(numPages)
            .fill()
            .map((_, i) => refs[i] || createRef()),
        );
      });
    }, [numPages]);

    useEffect(() => {
      if (!pdf || !aiExtractionResult) {
        return;
      }

      setReachedBoundingBoxesGrouped(aiExractionFieldsReachedByMouseGrouped);
    }, [pdf, aiExractionFieldsReachedByMouseGrouped, aiExtractionResult]);

    useEffect(() => {
      const canvas = aiModelResultOverlayRefs.length
        ? aiModelResultOverlayRefs[refIndex].current
        : null;
      const pdfCard = pdfCardRefs.length ? pdfCardRefs[refIndex].current : null;

      const isBoundingBoxesDisplayAllowed =
        canvas &&
        pdfCard &&
        !isLoading &&
        aiExtractionResult &&
        isAiExtractionResultDisplayed &&
        isFieldMappingMode;

      if (isBoundingBoxesDisplayAllowed) {
        const page = aiExractionFieldsGroupedByPage[`${PAGE_KEY}${currentPage}`];
        const { fields, size } = page || {};

        const pdfCardWidth = pdfCard.clientWidth;
        const pdfCardHeight = pdfCard.clientHeight;
        canvas.width = pdfCardWidth;
        canvas.height = pdfCardHeight;

        const context = canvas.getContext('2d');

        fields?.forEach(
          ({ boundingRegions, isMappedOnExtractionField, type: fieldNameContainer }) => {
            const boundingBoxColors = getBoundingBoxColors({
              theme,
              isMappedOnExtractionField,
              isLayout: !fieldNameContainer,
            });

            boundingRegions.forEach(({ boundingPolygon }) =>
              displayFieldBoundingBox({
                boundingPolygon,
                pdfCard,
                context,
                boundingBoxColors,
                pageNumber: currentPage,
                pageSize: size,
              }),
            );
          },
        );

        return () => releaseCanvasBackingStore(canvas);
      }
    }, [
      isLoading,
      aiExtractionResult,
      isAiExtractionResultDisplayed,
      isFieldMappingMode,
      aiExractionFieldsGroupedByPage,
      aiModelResultOverlayRefs,
      pdfCardRefs,
      currentPage,
      refIndex,
      theme,
    ]);

    useEffect(() => {
      const currentPopUp = popUpRefs[refIndex];

      if (currentPopUp?.current && boundingBoxPopUpInfo) {
        const { coords, quadrant } = boundingBoxPopUpInfo;
        setBoundingBoxPopUpPosition({
          coords,
          quadrant,
          boundingBoxPopUp: currentPopUp,
        });
      }
    }, [boundingBoxPopUpInfo, selectedPage, popUpRefs, pdfCardRefs, currentPage, refIndex]);

    useEffect(() => {
      if (isAiExtractionResultDisplayed) {
        return () => setBoundingBoxPopUpInfo(null);
      }
    }, [currentPage, pdfCardRefs, setBoundingBoxPopUpInfo, isAiExtractionResultDisplayed]);

    // LOCATE mode: a fresh click on a field re-arms the highlight after a previous dismiss.
    useEffect(() => {
      setIsLocateDismissed(false);
    }, [locatedFieldSource]);

    // LOCATE mode: bring the clicked field's page into view (even when no box resolves).
    useEffect(() => {
      if (isFieldMappingMode || isLocateDismissed || !locatedFieldSource?.pageNumber) {
        return;
      }

      const { pageNumber } = locatedFieldSource;
      setState(prevState => ({ ...prevState, selectedPage: [pageNumber] }));
      scrollInPagesContainerToPage(pageNumber);
      scrollInThumbnailsContainerToPage(pageNumber);
    }, [
      isFieldMappingMode,
      isLocateDismissed,
      locatedFieldSource,
      scrollInPagesContainerToPage,
      scrollInThumbnailsContainerToPage,
    ]);

    // LOCATE mode: draw ONLY the located field's box on its page and build its value/confidence
    // popup; recomputed on zoom/rotate so the box tracks the rendered page.
    useEffect(() => {
      if (isFieldMappingMode) {
        return;
      }

      const canvas = aiModelResultOverlayRefs.length
        ? aiModelResultOverlayRefs[refIndex]?.current
        : null;
      const pdfCard = pdfCardRefs.length ? pdfCardRefs[refIndex]?.current : null;

      if (!canvas || !pdfCard) {
        return;
      }

      const boundingPolygon = locatedField?.field.boundingRegions?.[0]?.boundingPolygon;
      const isLocatedOnCurrentPage =
        locatedField && locatedField.pageNumber === currentPage && !isLoading;

      if (!isLocatedOnCurrentPage || !boundingPolygon?.length) {
        releaseCanvasBackingStore(canvas);
        setLocatedFieldPopUp(null);

        return;
      }

      const context = canvas.getContext('2d');
      canvas.width = pdfCard.clientWidth;
      canvas.height = pdfCard.clientHeight;
      context.clearRect(0, 0, canvas.width, canvas.height);

      const { field, pageSize } = locatedField;
      const boundingBoxColors = getBoundingBoxColors({
        theme,
        isMappedOnExtractionField: field.isMappedOnExtractionField,
        isLayout: false,
      });

      displayFieldBoundingBox({
        isMouseInBoundingBox: true,
        boundingPolygon,
        pdfCard,
        context,
        boundingBoxColors,
        pageSize,
      });

      const coords = getBoundingBoxCoords({ boundingPolygon, pdfCard, pageSize });
      const quadrant = getBoxQuadrant({
        x: coords.upperLeft.coordX,
        y: coords.upperLeft.coordY,
        pdfCard,
      });

      setLocatedFieldPopUp({
        coords,
        quadrant,
        extractionField: { name: locatedFieldSource?.fieldName },
        type: field.schema ?? field.type,
        value: field.value,
        confidence: field.confidence,
        itemValues: field.itemValues,
      });

      return () => releaseCanvasBackingStore(canvas);
    }, [
      isFieldMappingMode,
      locatedField,
      locatedFieldSource,
      currentPage,
      isLoading,
      aiModelResultOverlayRefs,
      pdfCardRefs,
      refIndex,
      theme,
      scale,
      rotationByIndex,
    ]);

    // LOCATE mode: position the located-field popup next to its box (mirrors the mapping-mode popup).
    useEffect(() => {
      const currentPopUp = popUpRefs[refIndex];

      if (currentPopUp?.current && locatedFieldPopUp) {
        setBoundingBoxPopUpPosition({
          coords: locatedFieldPopUp.coords,
          quadrant: locatedFieldPopUp.quadrant,
          boundingBoxPopUp: currentPopUp,
        });
      }
    }, [locatedFieldPopUp, popUpRefs, refIndex]);

    const onLocatedFieldPopUpClose = useCallback(() => {
      setIsLocateDismissed(true);
      setLocatedFieldPopUp(null);
    }, []);

    useEffect(() => {
      if (docId !== prevDocId) {
        setState(prevState => ({
          ...prevState,
          scale: DOCUMENT_DEFAULT_SCALE,
          rotationByIndex: [],
          selectedPage: [1],
          pageOrder: null,
          isReorderSaving: false,
        }));
        savedIndex.current = 1;

        if (!_.isNil(isThumbnailsVisible)) {
          setIsPagesListOpen(isThumbnailsVisible);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [docId, prevDocId]);

    useEffectSafeStrictMode(() => {
      const loadState = { isSuperseded: false };

      const getPDF = async () => {
        try {
          setIsLoading(true);
          setReadingAnchor(null);
          setState(prevState => getUnloadedDocumentState(prevState, initialSelectedPage));

          const { data: pdfBlob } = await getDocumentCall({ documentId: docId });
          const pdfData = await pdfBlob.arrayBuffer();

          if (loadState.isSuperseded) {
            return;
          }

          const PDFDocumentLoadingTask = await getPdfJsDocument({ data: pdfData });

          // The cleanup only destroys the task in the ref, so a task created after it ran must
          // be destroyed here or its worker leaks.
          if (loadState.isSuperseded) {
            PDFDocumentLoadingTask.promise.catch(_.noop);
            PDFDocumentLoadingTask.destroy();

            return;
          }

          PDFDocumentLoadingTaskRef.current = PDFDocumentLoadingTask;

          const pdf = await PDFDocumentLoadingTask.promise;

          if (loadState.isSuperseded) {
            return;
          }

          const pageBaseSizes = await getPdfPageBaseSizes(pdf);

          if (loadState.isSuperseded) {
            return;
          }

          const scale = await getDocumentInitialScale({
            pdf,
            containerRef: pdfPagesWrapperRef,
            isTurnedOn: isLowerLargeScreenRef.current,
            pageBaseSizes,
          });

          if (loadState.isSuperseded) {
            return;
          }

          setReadingAnchor(getInitialReadingAnchor(initialSelectedPage));
          setState(prevState => ({
            ...prevState,
            pdf,
            pageBaseSizes,
            scale,
            selectedPage: initialSelectedPage ? [initialSelectedPage] : prevState.selectedPage,
            pageOrder: null,
          }));
          pendingThumbnailScrollPageRef.current = initialSelectedPage;

          if (onDocumentLoaded) {
            onDocumentLoaded();
          }
        } catch (err) {
          if (loadState.isSuperseded) {
            return;
          }

          if (!supressErrorMessage) {
            dispatch(showMessageAction({ message: 'Could not get PDF', variant: 'error' }));
          }

          PDFDocumentLoadingTaskRef.current?.destroy();
          PDFDocumentLoadingTaskRef.current = null;
          resetDocumentId();
          logger.error(err);
        } finally {
          setIsLoading(false);
        }
      };

      if (isMalwareBlocked) {
        setReadingAnchor(null);
        setState(prevState => getUnloadedDocumentState(prevState, initialSelectedPage));
        setIsLoading(false);
      }

      if (docId && !isMalwareBlocked) {
        getPDF();
      }

      // Each getDocument spawns its own worker; destroying the task is what releases it.
      return () => {
        loadState.isSuperseded = true;
        // A page save still loading must see the document as gone, or it adopts a task that
        // nothing will destroy after unmount.
        currentPdfRef.current = null;
        PDFDocumentLoadingTaskRef.current?.destroy();
        PDFDocumentLoadingTaskRef.current = null;
      };
      // scroll callbacks and initialSelectedPage is omitted intentionally for first render
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [docId, dispatch, setIsLoading, getDocumentCall, resetDocumentId, isMalwareBlocked]);

    const onBoundingBoxPopUpClose = useCallback(
      () => setBoundingBoxPopUpInfo(null),
      [setBoundingBoxPopUpInfo],
    );

    const getPdfData = useCallback(
      () =>
        new Promise((resolve, reject) => {
          if (pdf) {
            pdf.getData().then(async pdfData => {
              const { PDFDocument } = await import('pdf-lib');

              PDFDocument.load(pdfData, { ignoreEncryption: true }).then(pdfLibDoc => {
                if (pdfLibDoc.isEncrypted) {
                  reject('Selected document is encrypted and cannot be edited!');
                }

                resolve(pdfLibDoc);
              });
            });
          }
        }),
      [pdf],
    );

    const updatePdf = useCallback(
      pdfArray => {
        const fd = new FormData();
        // documentsapi takes the multipart filename as the document's name; an unnamed Blob part
        // arrives as "blob" and renames the document.
        fd.append(
          'file',
          new Blob([pdfArray], { type: 'application/pdf' }),
          ...getConditionalArray(!!documentFileName, [documentFileName]),
        );

        fd.append('body', jsonStringify({ id: docId }));

        return documentsApi.editContent({ formData: fd, documentId: docId });
      },
      [docId, documentFileName],
    );

    const deletePage = useCallback(
      (pageNumber, isNeedReselectPage) => {
        const currentOrder = pageOrder ?? naturalPageOrder;

        if (!pdf || currentOrder.length < 2 || isPageEditInFlight) {
          return;
        }

        const previousPageOrder = pageOrder;
        const isDeletingPrev = selectedPage.filter(sp => sp > pageNumber).length > 0;
        const newOrder = currentOrder.filter((__, index) => index !== pageNumber - 1);

        setState(prevState => {
          const nextSelectedPage = isDeletingPrev
            ? prevState.selectedPage.map(sp => sp - 1)
            : prevState.selectedPage;

          return {
            ...prevState,
            pageOrder: newOrder,
            selectedPage: isNeedReselectPage ? [1] : nextSelectedPage,
            isReorderSaving: true,
          };
        });

        getPdfData()
          .then(async pdfLibDoc => {
            const { PDFDocument } = await import('pdf-lib');
            const newPdfDoc = await PDFDocument.create();
            const copiedPages = await newPdfDoc.copyPages(pdfLibDoc, newOrder);
            copiedPages.forEach(page => newPdfDoc.addPage(page));
            const changedPdf = await newPdfDoc.save();

            const { data } = await updatePdf(changedPdf);
            dispatch(updateDocumentContentAction(data));
            dispatch(
              showMessageAction({
                message: 'Page was successfully deleted',
                variant: 'success',
              }),
            );
            setState(prevState => ({ ...prevState, isReorderSaving: false }));
            scrollInPagesContainerToPage(1);
          })
          .catch(err => {
            setState(prevState => {
              if (!prevState.isReorderSaving) {
                return prevState;
              }

              return {
                ...prevState,
                pageOrder: previousPageOrder,
                isReorderSaving: false,
              };
            });
            dispatch(showMessageAction({ message: 'Could not delete the page', variant: 'error' }));
            logger.error(err);
          });
      },
      [
        pdf,
        pageOrder,
        naturalPageOrder,
        isPageEditInFlight,
        dispatch,
        updatePdf,
        getPdfData,
        selectedPage,
        scrollInPagesContainerToPage,
      ],
    );

    const deletePages = useCallback(
      pageNumbers => {
        const currentOrder = pageOrder ?? naturalPageOrder;

        if (!pdf || currentOrder.length < 2 || isPageEditInFlight) {
          return;
        }

        const previousPageOrder = pageOrder;
        const newOrder = currentOrder.filter((__, index) => !pageNumbers.includes(index));

        setState(prevState => ({
          ...prevState,
          pageOrder: newOrder,
          isReorderSaving: true,
        }));

        getPdfData()
          .then(async pdfLibDoc => {
            const { PDFDocument } = await import('pdf-lib');
            const newPdfDoc = await PDFDocument.create();
            const copiedPages = await newPdfDoc.copyPages(pdfLibDoc, newOrder);
            copiedPages.forEach(page => newPdfDoc.addPage(page));
            const changedPdf = await newPdfDoc.save();

            const { data } = await updatePdf(changedPdf);
            dispatch(updateDocumentContentAction(data));
            setState(prevState => ({ ...prevState, isReorderSaving: false }));
          })
          .catch(err => {
            setState(prevState => {
              if (!prevState.isReorderSaving) {
                return prevState;
              }

              return {
                ...prevState,
                pageOrder: previousPageOrder,
                isReorderSaving: false,
              };
            });
            dispatch(showMessageAction({ message: 'Could not delete the page', variant: 'error' }));
            logger.error(err);
          });
      },
      [pdf, pageOrder, naturalPageOrder, isPageEditInFlight, dispatch, updatePdf, getPdfData],
    );

    const setSelectedPage = useCallback(
      page => setState(prevState => ({ ...prevState, selectedPage: [page] })),
      [setState],
    );

    const setDocumentSlideRef = useCallback(
      refCallback => element => {
        refCallback?.(element);
        thumbnailsWrapperRef.current = element;
      },
      [],
    );

    const adoptSavedPdf = useCallback(async (savedPdf, replacedPdf) => {
      const savedPdfLoadingTask = await getPdfJsDocument({ data: savedPdf });
      const { pdf: pdfJsDoc, pageBaseSizes: savedPageBaseSizes } =
        await loadPdfWithBaseSizes(savedPdfLoadingTask);

      if (currentPdfRef.current !== replacedPdf) {
        savedPdfLoadingTask.destroy();

        return;
      }

      PDFDocumentLoadingTaskRef.current?.destroy();
      PDFDocumentLoadingTaskRef.current = savedPdfLoadingTask;

      setState(prevState => ({
        ...prevState,
        pdf: pdfJsDoc,
        pageBaseSizes: savedPageBaseSizes,
        rotationByIndex: [],
        pageOrder: null,
      }));
    }, []);

    const onSavePage = useCallback(() => {
      if (isPageEditInFlight) {
        return;
      }

      const currentOrder = pageOrder ?? naturalPageOrder;
      setState(prevState => ({ ...prevState, isPageSaving: true }));

      getPdfData()
        .then(async pdfLibDoc => {
          const { PDFDocument } = await import('pdf-lib');
          const newPdfDoc = await PDFDocument.create();
          const copiedPages = await newPdfDoc.copyPages(pdfLibDoc, currentOrder);
          copiedPages.forEach(page => newPdfDoc.addPage(page));

          rotationByIndex.forEach((rotation, displayIdx) => {
            if (rotation === undefined || displayIdx >= copiedPages.length) {
              return;
            }

            const page = newPdfDoc.getPage(displayIdx);
            const { type, angle } = page.getRotation();

            page.setRotation({ type, angle: angle + rotation });
          });

          const changedPdf = await newPdfDoc.save();
          // Upload before pdf.js sees the bytes: pdf.js transfers the buffer to its worker,
          // which detaches changedPdf and would turn the upload into an empty file.
          const { data } = await updatePdf(changedPdf);

          try {
            await adoptSavedPdf(changedPdf, pdf);
          } catch (err) {
            logger.error(err);
          }

          setState(prevState => ({ ...prevState, isPageSaving: false }));
          dispatch(updateDocumentContentAction(data));
          dispatch(
            showMessageAction({
              message: 'Page was successfully saved',
              variant: 'success',
            }),
          );
        })
        .catch(err => {
          setState(prevState => ({ ...prevState, isPageSaving: false }));
          dispatch(showMessageAction({ message: 'Could not save the page', variant: 'error' }));
          logger.error(err);
        });
    }, [
      pdf,
      pageOrder,
      naturalPageOrder,
      rotationByIndex,
      dispatch,
      updatePdf,
      getPdfData,
      adoptSavedPdf,
      isPageEditInFlight,
    ]);

    const onPrevPage = useCallback(
      e => {
        e.preventDefault();

        let nextPage;

        setState(prevState => {
          nextPage = firstSelectedPage;

          if (firstSelectedPage >= 2) {
            nextPage = firstSelectedPage - 1;
          }

          return {
            ...prevState,
            selectedPage: [nextPage],
          };
        });

        if (nextPage !== selectedPage) {
          scrollInPagesContainerToPage(nextPage);
          scrollInThumbnailsContainerToPage(nextPage);
        }
      },
      [
        selectedPage,
        firstSelectedPage,
        scrollInPagesContainerToPage,
        scrollInThumbnailsContainerToPage,
      ],
    );

    const onNextPage = useCallback(
      e => {
        e.preventDefault();

        let nextPage;

        setState(prevState => {
          nextPage = firstSelectedPage;

          if (prevState.selectedPage !== numPages) {
            nextPage = firstSelectedPage + 1;
          }

          return {
            ...prevState,
            selectedPage: [nextPage],
          };
        });

        if (nextPage !== selectedPage) {
          scrollInPagesContainerToPage(nextPage);
          scrollInThumbnailsContainerToPage(nextPage);
        }
      },
      [
        numPages,
        selectedPage,
        firstSelectedPage,
        scrollInPagesContainerToPage,
        scrollInThumbnailsContainerToPage,
      ],
    );

    const onRotateLeft = useCallback(e => {
      e.preventDefault();

      setState(prevState => {
        const pageIndex = prevState.selectedPage[0] - 1;
        const newRotationByIndex = [...prevState.rotationByIndex];

        newRotationByIndex[pageIndex] ??= 0;
        newRotationByIndex[pageIndex] -= DOCUMENT_ROTATION_STEP;
        const isLessMinPageRotation = newRotationByIndex[pageIndex] < 0;

        if (isLessMinPageRotation) {
          newRotationByIndex[pageIndex] = DOCUMENT_MAX_ROTATION;
        }

        return { ...prevState, rotationByIndex: newRotationByIndex };
      });
    }, []);

    const onRotateRight = useCallback(e => {
      e.preventDefault();

      setState(prevState => {
        const pageIndex = prevState.selectedPage[0] - 1;
        const newRotationByIndex = [...prevState.rotationByIndex];

        newRotationByIndex[pageIndex] ??= 0;
        newRotationByIndex[pageIndex] += DOCUMENT_ROTATION_STEP;
        const isMoreMaxPageRotation = newRotationByIndex[pageIndex] > DOCUMENT_MAX_ROTATION;

        if (isMoreMaxPageRotation) {
          newRotationByIndex[pageIndex] = 0;
        }

        return { ...prevState, rotationByIndex: newRotationByIndex };
      });
    }, []);

    const onRotateDocument = useCallback(
      async e => {
        if (isPageEditInFlight) {
          return;
        }

        const { direction } = e.currentTarget.dataset;

        setState(prevState => {
          const newRotationByIndex = Array.from({ length: displayedPageCount }).map(
            (__, pageIndex) => {
              let rotationByIndex = prevState.rotationByIndex[pageIndex] ?? 0;

              switch (direction) {
                case DocumentRotationDirection.LEFT: {
                  rotationByIndex -= DOCUMENT_ROTATION_STEP;
                  rotationByIndex = rotationByIndex < 0 ? DOCUMENT_MAX_ROTATION : rotationByIndex;
                  break;
                }

                case DocumentRotationDirection.RIGHT: {
                  rotationByIndex += DOCUMENT_ROTATION_STEP;
                  rotationByIndex = rotationByIndex > DOCUMENT_MAX_ROTATION ? 0 : rotationByIndex;
                  break;
                }

                default:
                  break;
              }

              return rotationByIndex;
            },
          );

          return { ...prevState, rotationByIndex: newRotationByIndex };
        });
      },
      [displayedPageCount, isPageEditInFlight],
    );

    const onZoomIn = useCallback(e => {
      e.preventDefault();

      setState(prevState => {
        let currentScale;

        if (prevState.scale >= _.last(SCALES)) {
          currentScale = prevState.scale;
        } else {
          const currentScaleIndex = _.findIndex(SCALES, scale => scale === prevState.scale);
          currentScale = SCALES[currentScaleIndex + 1];
        }

        return {
          ...prevState,
          scale: currentScale,
        };
      });
    }, []);

    const onZoomOut = useCallback(e => {
      e.preventDefault();

      setState(prevState => {
        let currentScale;

        if (prevState.scale <= _.first(SCALES)) {
          currentScale = prevState.scale;
        } else {
          const currentScaleIndex = _.findIndex(SCALES, scale => scale === prevState.scale);
          currentScale = SCALES[currentScaleIndex - 1];
        }

        return {
          ...prevState,
          scale: currentScale,
        };
      });
    }, []);

    const onSelectScale = useCallback(e => {
      e.preventDefault();

      const { value } = e.target;

      setState(prevState => ({
        ...prevState,
        scale: value,
      }));
    }, []);

    const onSelectPage = useCallback(
      e => {
        e.preventDefault();
        const index = +e.target.dataset.index;
        const pageNumber = index + 1;
        const currentSavedIndex = savedIndex.current;

        let selectedPages;

        if (e.shiftKey) {
          const selectedPagesRange =
            currentSavedIndex < pageNumber
              ? [currentSavedIndex, pageNumber + 1]
              : [pageNumber, currentSavedIndex + 1];

          selectedPages = _.range(...selectedPagesRange);
        } else {
          savedIndex.current = pageNumber;
          selectedPages = [pageNumber];
          scrollInPagesContainerToPage(pageNumber);
        }

        setState(prevState => ({
          ...prevState,
          readingPageIndex: e.shiftKey ? prevState.readingPageIndex : index,
          selectedPage: selectedPages,
        }));
      },
      [scrollInPagesContainerToPage],
    );

    const onDeletePage = useCallback(
      e => {
        e.preventDefault();
        e.stopPropagation();

        const { page } = e.target.dataset;
        let selectedPageForDeleting = selectedPage;

        if (page) {
          selectedPageForDeleting = +page;
        }
        const isNeedReselect = _.includes(selectedPage, selectedPageForDeleting);

        const params = {
          type: ConfirmationModalType.WARNING,
          message: 'Confirm document page delete',
          description: 'Are you sure you want to delete this document page?',
          onAccept: () => deletePage(selectedPageForDeleting, isNeedReselect),
        };

        dispatch(openConfirmationDialogAction(params));
      },
      [selectedPage, deletePage, dispatch],
    );

    const onSplit = useCallback(async () => {
      try {
        setIsLoading(true);

        const currentOrder = pageOrder ?? naturalPageOrder;
        const displayIndicesToSplit = selectedPage.map(page => page - 1);
        const origIndicesToCopy = displayIndicesToSplit.map(idx => currentOrder[idx]);

        const { PDFDocument } = await import('pdf-lib');
        const pdfData = await getPdfData();
        const pdfDoc = await PDFDocument.create();
        const copiedPages = await pdfDoc.copyPages(pdfData, origIndicesToCopy);
        copiedPages?.forEach(page => {
          pdfDoc.addPage(page);
        });
        const pdfBytes = await pdfDoc.save();

        const fd = new FormData();
        fd.append('file', new Blob([pdfBytes], { type: 'application/pdf' }));

        const params = { ...loanParams, formData: fd, documentTypeId };

        const {
          data: { id: documentId },
        } = await documentApi.createDocumentV2(params);

        deletePages(displayIndicesToSplit);
        dispatch(getDocumentByIdAction({ documentId }));

        if (afterSplitCb) {
          afterSplitCb(documentId);
        }
      } catch (err) {
        dispatch(showMessageAction({ message: err.toString(), variant: 'error' }));
        logger.error(err);
      } finally {
        setIsLoading(false);
      }
    }, [
      documentTypeId,
      loanParams,
      selectedPage,
      pageOrder,
      naturalPageOrder,
      afterSplitCb,
      dispatch,
      getPdfData,
      setIsLoading,
      deletePages,
    ]);

    const onTogglePages = useCallback(
      async e => {
        e.preventDefault();
        e.stopPropagation();
        setIsPagesListOpen(!isPagesListOpen);

        const scale = await getDocumentInitialScale({
          pdf,
          containerRef: pdfPagesWrapperRef,
          isTurnedOn: isLowerLargeScreenRef.current,
        });

        if (currentPdfRef.current !== pdf) {
          return;
        }

        setState(prevState => ({
          ...prevState,
          scale,
        }));
      },
      [isPagesListOpen, pdf],
    );

    const onDragEnd = useCallback(
      ({ destination, source }) => {
        if (!pdf || source.index === destination?.index || !destination) {
          return;
        }

        if (isPageEditInFlight) {
          return;
        }

        const previousPageOrder = pageOrder;
        const currentOrder = pageOrder ?? naturalPageOrder;
        const newOrder = reorder({
          list: currentOrder,
          startIndex: source.index,
          endIndex: destination.index,
        });
        const targetPageNumber = destination.index + 1;

        setState(prevState => ({
          ...prevState,
          pageOrder: newOrder,
          selectedPage: [targetPageNumber],
          isReorderSaving: true,
        }));

        getPdfData()
          .then(async pdfLibDoc => {
            const { PDFDocument } = await import('pdf-lib');
            const newPdfDoc = await PDFDocument.create();
            const copiedPages = await newPdfDoc.copyPages(pdfLibDoc, newOrder);
            copiedPages.forEach(page => newPdfDoc.addPage(page));
            const changedPdf = await newPdfDoc.save();

            const { data } = await updatePdf(changedPdf);
            dispatch(updateDocumentContentAction(data));
            setState(prevState => ({ ...prevState, isReorderSaving: false }));
            scrollInPagesContainerToPage(targetPageNumber);
          })
          .catch(err => {
            setState(prevState => {
              if (!prevState.isReorderSaving) {
                return prevState;
              }

              return {
                ...prevState,
                pageOrder: previousPageOrder,
                isReorderSaving: false,
              };
            });
            dispatch(
              showMessageAction({ message: 'Could not rearrange this page', variant: 'error' }),
            );
            logger.error(err);
          });
      },
      [
        pdf,
        pageOrder,
        naturalPageOrder,
        isPageEditInFlight,
        dispatch,
        getPdfData,
        updatePdf,
        scrollInPagesContainerToPage,
      ],
    );

    const onSubmitPageNumber = useCallback(
      pageNumber => {
        setSelectedPage(pageNumber);

        scrollInPagesContainerToPage(pageNumber);
        scrollInThumbnailsContainerToPage(pageNumber);
      },
      [setSelectedPage, scrollInPagesContainerToPage, scrollInThumbnailsContainerToPage],
    );

    const onPagesContainerScroll = useMemo(
      () =>
        _.throttle(
          () => {
            const pageNumber = getMostVisiblePageNumber(pdfPagesWrapperRef.current);

            if (!pageNumber) {
              return;
            }

            // A re-anchor after zoom/rotate is not navigation: it must not collapse a range
            // selection or scroll the thumbnail rail.
            const isSelectionChanged =
              !checkIsRestoreScroll() && selectedPageRef.current?.[0] !== pageNumber;

            setState(prevState =>
              getScrolledPageState(prevState, { pageNumber, isSelectionChanged }),
            );

            if (isSelectionChanged) {
              scrollInThumbnailsContainerToPage(pageNumber);
            }
          },
          50,
          { leading: true, trailing: true },
        ),
      [checkIsRestoreScroll, scrollInThumbnailsContainerToPage],
    );

    const onPagesScroll = useCallback(() => {
      onReadingAnchorScroll();
      onPagesContainerScroll();
    }, [onReadingAnchorScroll, onPagesContainerScroll]);

    useEffect(() => () => onPagesContainerScroll.cancel(), [onPagesContainerScroll]);

    useImperativeHandle(
      transferRef,
      () => ({
        onRotateDocument,
        onSavePage,
        onSplit,
        isDisabledSplitButton,
        wrapperRef,
      }),
      [onRotateDocument, onSavePage, onSplit, isDisabledSplitButton],
    );

    if (isDocumentViewNotRendered) {
      return null;
    }

    const renderThumbnailOverlay = index => {
      if (displayedPageCount < 2) {
        return null;
      }

      return (
        <div
          className={clsx(
            classes.thumbnailOverlay,
            'flex justify-center items-center document-thumbnail-overlay-container',
          )}
          data-testid={DocumentTestIds.THUMBNAIL_OVERLAY}
        >
          <ButtonWithTooltip
            data-page={index + 1}
            data-testid={DocumentTestIds.DOCUMENT_PREVIEW_OVERLAY_DELETE_PAGE_BUTTON}
            icon={DeleteIcon}
            iconButtonClassName="[&>*]:pointer-events-none"
            iconClassName="tool__disable-interaction"
            iconSize={IconSize[24]}
            size="small"
            title="Delete"
            variant="outlined"
            onClick={onDeletePage}
          />
        </div>
      );
    };

    return (
      <Box className={clsx('flex flex-1 h-full')} ref={wrapperRef} sx={documentViewWrapperSx}>
        <Box className="flex flex-col" sx={classes.documentSlideContainer}>
          <ThumbnailControlPanel
            disabledSplit={isDisabledSplitButton}
            isPagesList={isPagesListVisible}
            onSplit={onSplit}
            onToggle={onTogglePages}
          />

          {isLoading && isPagesListVisible && (
            <Skeleton height={265} length={3} type={SKELETON_TYPES.PAPER_SMALL} width={240} />
          )}

          {!isLoading && isPagesListVisible && (
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable
                direction="vertical"
                droppableId="documentPages"
                isDropDisabled={!isControlVisible}
                type="documentPages"
              >
                {provided => (
                  <Box
                    className={clsx(
                      'overflow-y-auto overflow-x-hidden',
                      'document_view__document_slide',
                    )}
                    ref={setDocumentSlideRef(provided.innerRef)}
                    sx={documentSlideSx}
                  >
                    {effectivePageOrder.map((origIdx, displayIdx) => {
                      const pageNumber = displayIdx + 1;
                      const isSelected = selectedPage.some(sp => sp === pageNumber);

                      return (
                        <Draggable
                          draggableId={`documentPage-${origIdx}`}
                          index={displayIdx}
                          isDragDisabled={isPageEditInFlight}
                          key={`${docId}-${origIdx}`}
                          type="documentPages"
                        >
                          {provided => (
                            <Box
                              ref={provided.innerRef}
                              sx={classes.thumbnailCard}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                            >
                              <div
                                data-index={displayIdx}
                                data-testid={DocumentTestIds.THUMBNAIL}
                                id={getThumbnailAnchorId(pageNumber)}
                                tabIndex={0}
                                onFocus={onSelectPage}
                                onMouseDown={onSelectPage}
                              >
                                <PdfCard
                                  isThumbnail
                                  data-testid={`document_preview.pdf_card-${pageNumber}${
                                    isSelected ? '-selected' : ''
                                  }`}
                                  isSelected={isSelected}
                                  num={origIdx + 1}
                                  pageSize={pageBaseSizes?.[origIdx]}
                                  pdf={pdf}
                                  scale={0.3}
                                />
                                <Box sx={classes.pageNumber}>{pageNumber}</Box>
                              </div>

                              {isControlVisible && renderThumbnailOverlay(displayIdx)}
                            </Box>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </Box>

        <Box className="flex flex-col grow" sx={documentViewContainerSx}>
          <ControlPanel
            checkedAiLayers={checkedAiLayers}
            docId={docId}
            isControlVisible={isControlVisible}
            isDownloadable={isDownloadable && !isMalwareBlocked}
            isFocusMode={isFocusMode}
            isNavigateDisabled={selectedPage.length > 1}
            isOpenInNewTabAllowed={isOpenInNewTabAllowed && !isMalwareBlocked}
            isPageSaving={isPageSaving}
            isPrintable={isPrintable && !isMalwareBlocked}
            isSavePageButtonDisabled={!isPagesRotated || isPageEditInFlight}
            isViewHistoryAvailable={isViewHistoryAvailable}
            pagesNumber={displayedPageCount}
            scale={scale}
            selectedPage={firstSelectedPage}
            onDownloadDocument={onDownloadDocument}
            onNextPage={onNextPage}
            onOpenDocumentInNewTab={handleOpenInNewTab}
            onPrevPage={onPrevPage}
            onPrintDocument={onPrintDocument}
            onRotateLeft={onRotateLeft}
            onRotateRight={onRotateRight}
            onSavePage={onSavePage}
            onSelectAiLayer={onSelectAiLayer}
            onSelectPage={onSubmitPageNumber}
            onSelectScale={onSelectScale}
            onToggleFocus={onToggleFocus}
            onZoomIn={onZoomIn}
            onZoomOut={onZoomOut}
          />

          <Box
            className={clsx(
              'overflow-y-auto flex',
              'document_view__document_pages_wrapper',
              'flex-col items-start relative',
            )}
            ref={pdfPagesWrapperRef}
            sx={documentViewSx}
            onScroll={onPagesScroll}
          >
            {isMalwareBlocked && <DocumentBlockedNotice />}

            {isLoading ? (
              <Skeleton height={800} length={1} type={SKELETON_TYPES.PAPER_HUGE} width={620} />
            ) : (
              effectivePageOrder.map((origIdx, displayIdx) => {
                const pageNumber = origIdx + 1;

                return (
                  <div className="relative" key={`${docId}-${origIdx}`}>
                    {isAiExtractionResultDisplayed && (
                      <>
                        <AiModelResultOverlay
                          ref={aiModelResultOverlayRefs[displayIdx]}
                          onMouseMove={isFieldMappingMode ? onMouseMove : null}
                        />

                        {isFieldMappingMode
                          ? boundingBoxPopUpInfo && (
                              <BoundingBoxPopUp
                                popUpInfo={boundingBoxPopUpInfo}
                                ref={popUpRefs[displayIdx]}
                                setAiModelFieldToFillInForm={setAiModelFieldToFillInForm}
                                onClose={onBoundingBoxPopUpClose}
                              />
                            )
                          : locatedFieldPopUp &&
                            displayIdx === refIndex && (
                              <BoundingBoxPopUp
                                popUpInfo={locatedFieldPopUp}
                                ref={popUpRefs[displayIdx]}
                                onClose={onLocatedFieldPopUpClose}
                              />
                            )}
                      </>
                    )}

                    <PdfCard
                      isOffscreenReleaseEnabled
                      canvasRef={pdfCardRefs[displayIdx]}
                      data-testid={DocumentTestIds.DOCUMENT_PREVIEW_PAGE_CANVAS}
                      isInRenderWindow={checkIsPageInRenderWindow(displayIdx, readingPageIndex)}
                      num={pageNumber}
                      pageSize={pageBaseSizes?.[origIdx]}
                      pdf={pdf}
                      rotation={rotationByIndex[displayIdx]}
                      scale={scale}
                    />
                  </div>
                );
              })
            )}
          </Box>
        </Box>
      </Box>
    );
  },
);

DocumentView.propTypes = {
  loanParams: PropTypes.object,
  docId: PropTypes.string,
  contentWidth: PropTypes.number,
  isView: PropTypes.bool,
  isControlVisible: PropTypes.bool,
  isDownloadable: PropTypes.bool,
  isFocusMode: PropTypes.bool,
  isPrintable: PropTypes.bool,
  isOpenInNewTabAllowed: PropTypes.bool,
  isViewHistoryAvailable: PropTypes.bool,
  isAiExtractionResultDisplayed: PropTypes.bool,
  locatedFieldSource: PropTypes.shape({
    fieldId: PropTypes.string,
    fieldName: PropTypes.string,
    fieldValue: PropTypes.string,
    pageNumber: PropTypes.number,
  }),
  isThumbnailsVisible: PropTypes.bool,
  supressErrorMessage: PropTypes.bool,
  initialSelectedPage: PropTypes.number,
  transferRef: PropTypes.shape({ current: PropTypes.object }),
  afterSplitCb: PropTypes.func,
  viewWrapperClassName: PropTypes.string,
  classes: PropTypes.object,
  resetDocumentId: PropTypes.func,
  getDocumentCall: PropTypes.func,
  onDownloadDocument: PropTypes.func,
  onPrintDocument: PropTypes.func,
  onOpenDocumentInNewTab: PropTypes.func,
  onDocumentLoaded: PropTypes.func,
  onToggleFocus: PropTypes.func,
  ...WithLoadingPropTypes,
};

const DocumentViewWithForwardRef = forwardRef((props, ref) => (
  <DocumentView {...props} transferRef={ref} />
));

export default memo(DocumentViewWithForwardRef);
